import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../config/firebase/config';
import { Restaurant } from '../../../types/restaurant';
import { ArrowLeft, DollarSign, CreditCard, TrendingUp, Users } from 'lucide-react';

interface Transaction {
  id: string;
  amount: number;
  tip: number;
  tax: number;
  status: 'completed' | 'failed' | 'pending';
  processor: string;
  createdAt: Date;
}

export default function RestaurantStatisticsScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    if (!id) return;
    try {
      // Load restaurant
      const restaurantDoc = await getDoc(doc(db, 'restaurants', id));
      if (restaurantDoc.exists()) {
        setRestaurant({ id: restaurantDoc.id, ...restaurantDoc.data() } as Restaurant);
      }
      // Load transactions from restaurant-scoped path
      const transactionsRef = collection(db, 'restaurants', id, 'transactions');
      const transactionsSnap = await getDocs(transactionsRef);
      const txData = transactionsSnap.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data(),
        createdAt: (docSnap.data() as any).createdAt?.toDate() || new Date(),
      })) as Transaction[];
      setTransactions(txData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate stats
  const stats = {
    totalRevenue: transactions
      .filter(t => t.status === 'completed')
      .reduce((sum, t) => sum + t.amount, 0),
    totalTransactions: transactions.length,
    successfulTransactions: transactions.filter(t => t.status === 'completed').length,
    failedTransactions: transactions.filter(t => t.status === 'failed').length,
    totalTips: transactions
      .filter(t => t.status === 'completed')
      .reduce((sum, t) => sum + (t.tip || 0), 0),
    averageTransaction: transactions.filter(t => t.status === 'completed').length > 0
      ? transactions
          .filter(t => t.status === 'completed')
          .reduce((sum, t) => sum + t.amount, 0) /
        transactions.filter(t => t.status === 'completed').length
      : 0,
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-DO', {
      style: 'currency',
      currency: restaurant?.settings?.currency || 'DOP'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando estadísticas...</p>
        </div>
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div className="p-8">
        <div className="text-center">
          <p className="text-red-600">Restaurante no encontrado</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <button
          onClick={() => navigate(`/super-admin/restaurants/${id}`)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft size={20} />
          Volver a los detalles del restaurante
        </button>
      </div>

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">{restaurant.name}</h1>
        <p className="text-gray-600 mt-1">Estadísticas de rendimiento</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Total Revenue */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <DollarSign className="text-green-600" size={24} />
            </div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900">{formatCurrency(stats.totalRevenue)}</h3>
          <p className="text-sm text-gray-600 mt-1">Total de ingresos</p>
        </div>

        {/* Total Transactions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <CreditCard className="text-blue-600" size={24} />
            </div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900">{stats.totalTransactions}</h3>
          <p className="text-sm text-gray-600 mt-1">Total de transacciones</p>
          <div className="flex gap-4 mt-2">
            <span className="text-xs text-green-600">✓ {stats.successfulTransactions} exitosas</span>
            <span className="text-xs text-red-600">✗ {stats.failedTransactions} fallidas</span>
          </div>
        </div>

        {/* Average Transaction */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-purple-100 rounded-lg">
              <TrendingUp className="text-purple-600" size={24} />
            </div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900">{formatCurrency(stats.averageTransaction)}</h3>
          <p className="text-sm text-gray-600 mt-1">Promedio de transacción</p>
        </div>

        {/* Total Tips */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-yellow-100 rounded-lg">
              <Users className="text-yellow-600" size={24} />
            </div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900">{formatCurrency(stats.totalTips)}</h3>
          <p className="text-sm text-gray-600 mt-1">Total de propinas</p>
        </div>
      </div>

      

      {/* Recent Transactions Preview */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">Transacciones recientes</h2>
          <button
            onClick={() => navigate(`/super-admin/restaurants/${id}/transactions`)}
            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            Ver todas →
          </button>
        </div>
        <div className="space-y-3">
          {transactions.slice(0, 5).map(tx => (
            <div key={tx.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">{formatCurrency(tx.amount)}</p>
                <p className="text-xs text-gray-500">{tx.createdAt.toLocaleString('es-ES')}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 rounded text-xs font-semibold ${
                  tx.status === 'completed' ? 'bg-green-100 text-green-700' :
                  tx.status === 'failed' ? 'bg-red-100 text-red-700' :
                  'bg-yellow-100 text-yellow-700'
                }`}>
                  {tx.status}
                </span>
              </div>
            </div>
          ))}
          {transactions.length === 0 && (
            <p className="text-center text-gray-500 py-8">No hay transacciones aún</p>
          )}
        </div>
      </div>
    </div>
  );
}

